import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ApiKeyRole from "enums/ApiKeyRole";
import ApiKeyScope from "enums/ApiKeyScope";
import ApiKeyStatus from "enums/ApiKeyStatus";
import {
  GET_API_KEYS,
  GET_API_KEY_METRICS,
  CREATE_API_KEY,
  REVOKE_API_KEY,
  ROTATE_API_KEY,
  UPDATE_API_KEY,
} from "graphql/apiKeys";

export { ApiKeyRole, ApiKeyScope, ApiKeyStatus };

export interface ApiKey {
  id: string;
  scope: ApiKeyScope;
  ownerId: string;
  ownerType: ApiKeyScope;
  name: string;
  role: ApiKeyRole;
  createdByUserId?: string | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  expiresAt?: number | null;
  lastUsedAt?: number | null;
  revokedAt?: number | null;
  allowListIp?: string | null;
  rateLimitSustained?: number | null;
  rateLimitBurst?: number | null;
  status: ApiKeyStatus;
}

export interface ApiKeyMetricByRoute {
  route: string;
  calls: number;
  errors: number;
}

export interface ApiKeyMetric {
  id: string;
  apiKeyId: string;
  hourBucket: string;
  totalCalls: number;
  totalErrors: number;
  byRoute: ApiKeyMetricByRoute[];
}

export interface CreateApiKeyInput {
  scope: ApiKeyScope;
  ownerId: string;
  name: string;
  role: ApiKeyRole;
  expiresAt?: number | null;
  allowListIp?: string | null;
  rateLimitSustained?: number | null;
  rateLimitBurst?: number | null;
}

export interface UpdateApiKeyInput {
  name?: string;
  role?: ApiKeyRole;
  expiresAt?: number | null;
  allowListIp?: string | null;
  rateLimitSustained?: number | null;
  rateLimitBurst?: number | null;
}

interface UseApiKeysArgs {
  scope: ApiKeyScope;
  // Caller passes the owner id (companyId or platformId) — used to gate the
  // query while the user object is still loading and to refetch on owner change.
  ownerId?: string;
}

/**
 * High-level hook for the API key admin UI. Wraps the GraphQL mutations and
 * queries so components don't depend on Apollo directly.
 *
 * createApiKey/rotateApiKey resolve to `{apiKey, secret}` — callers MUST
 * surface the plaintext secret to the user immediately because it's never
 * recoverable later.
 */
export function useApiKeys({ scope, ownerId }: UseApiKeysArgs) {
  const { t } = useTranslation();
  const {
    data,
    loading: keysLoading,
    error: keysError,
    refetch,
  } = useQuery<{ apiKeys: ApiKey[] }>(GET_API_KEYS, {
    variables: { scope },
    // Skip until we know the owner id — avoids a flash of "no keys" before
    // the user object hydrates.
    skip: !ownerId,
    fetchPolicy: "cache-and-network",
  });

  const [createApiKeyMutation, { loading: creating }] =
    useMutation(CREATE_API_KEY);
  const [revokeApiKeyMutation, { loading: revoking }] =
    useMutation(REVOKE_API_KEY);
  const [rotateApiKeyMutation, { loading: rotating }] =
    useMutation(ROTATE_API_KEY);
  const [updateApiKeyMutation, { loading: updating }] =
    useMutation(UPDATE_API_KEY);

  // The list refresh must not decide whether the mutation succeeded. It used
  // to: a network blip on the refetch rejected the caller's promise after the
  // server had already acted, which on rotate meant the new secret was never
  // revealed and the old one was already dead.
  //
  // Swallowing it silently is the other half of that mistake, though: this hook
  // does not write the cache, so a failed refresh leaves a deactivated key
  // showing as Active until someone reloads. The action keeps its own verdict
  // and the staleness is reported separately.
  const refresh = async () => {
    try {
      await refetch();
    } catch (err: unknown) {
      console.error(err);
      toast.error(t("ApiKeyListRefreshFailed"));
    }
  };

  const createApiKey = async (
    input: CreateApiKeyInput,
  ): Promise<{ apiKey: ApiKey; secret: string }> => {
    const result = await createApiKeyMutation({ variables: { input } });
    await refresh();
    return result.data.createApiKey;
  };

  const revokeApiKey = async (id: string): Promise<boolean> => {
    const result = await revokeApiKeyMutation({ variables: { id } });
    await refresh();
    return !!result.data?.revokeApiKey;
  };

  const rotateApiKey = async (
    id: string,
  ): Promise<{ apiKey: ApiKey; secret: string }> => {
    const result = await rotateApiKeyMutation({ variables: { id } });
    await refresh();
    return result.data.rotateApiKey;
  };

  const updateApiKey = async (
    id: string,
    input: UpdateApiKeyInput,
  ): Promise<ApiKey> => {
    const result = await updateApiKeyMutation({ variables: { id, input } });
    await refresh();
    return result.data.updateApiKey;
  };

  return {
    apiKeys: data?.apiKeys || [],
    loading: keysLoading,
    error: keysError,
    refetch,
    createApiKey,
    revokeApiKey,
    rotateApiKey,
    updateApiKey,
    mutating: creating || revoking || rotating || updating,
  };
}

/**
 * Lazily fetch the 30-day metrics for a single API key. The hook is its own
 * export so the table/list view can render quickly without waiting for
 * metrics — the metrics view loads them on demand when expanded.
 */
export function useApiKeyMetrics(apiKeyId: string | null, days = 30) {
  const { data, loading, error, refetch } = useQuery<{
    apiKeyMetrics: ApiKeyMetric[];
  }>(GET_API_KEY_METRICS, {
    variables: { apiKeyId, days },
    skip: !apiKeyId,
    fetchPolicy: "cache-and-network",
  });

  return {
    metrics: data?.apiKeyMetrics || [],
    loading,
    error,
    refetch,
  };
}
